import { formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import {
  Button,
  FormikField as Field,
  GuidanceBox,
  H1,
  H3,
  HStack,
  Input,
  Text,
  VStack,
} from '@betterment/samba-react';
import { WorkflowLayout } from '@betterment/shared-components';
import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import Footer from '../../../../Shared/components-v2/footer/Footer';
import { desiredSpending, useRetirementStep } from '../FlowConfig';
import { RetirementPurposeCreationOptionDocument } from '../graphql/query.retirementPurposeCreationOption.generated';

type FormValues = Yup.InferType<typeof desiredSpending>;

export const DesiredSpendingStep: React.FC = () => {
  const navigate = useNavigate();

  const { data } = useGraphQLQuery(RetirementPurposeCreationOptionDocument, {
    throwOnError: true,
  });

  const { commit } = useRetirementStep('desiredSpending');

  const retirementGrowthOption = data?.purposeCreationOptions.find(
    option => option.__typename === 'RetirementGrowthPurposeCreationOption'
  );

  if (!data || !retirementGrowthOption) return null;

  const { defaultRetirementMonthlySpending, recommendedMonthlySpending } = retirementGrowthOption;

  const initialValues: FormValues = {
    desiredMonthlySpending: formatMoney(defaultRetirementMonthlySpending, 'raw'),
  };

  const handleSubmit = (formValues: FormValues) => {
    commit(formValues);

    void navigate('/add_goal');
  };

  return (
    <WorkflowLayout pageTitle="Retirement">
      <WorkflowLayout.Header workflowTitle={null} onClose={() => navigate('/')} onBack={() => navigate(-1)} />
      <WorkflowLayout.Content>
        <VStack css={{ gap: 'space.300' }}>
          <VStack css={{ gap: 'space.200' }}>
            <H1 css={{ textWrap: 'auto' }}>How much do you want to spend each month in retirement?</H1>
            <Text>We'll use this to help you figure out how much you need to save in total.</Text>
          </VStack>
          <Formik
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validationSchema={desiredSpending}
            validateOnChange={false}
          >
            <Form>
              <VStack css={{ gap: 'space.400' }}>
                <VStack css={{ gap: 'space.200' }}>
                  <Field name="desiredMonthlySpending">
                    <Field.Label>Desired monthly spending</Field.Label>
                    <Field.Control>
                      <Input mask="number" leadingSymbol="$" trailingSymbol="/month" />
                    </Field.Control>
                    <Field.ErrorMessage />
                  </Field>
                  <GuidanceBox css={{ maxWidth: '100%' }}>
                    <GuidanceBox.Icon />
                    {recommendedMonthlySpending.title && (
                      <GuidanceBox.Header>
                        <H3 variant="title100">{recommendedMonthlySpending.title}</H3>
                      </GuidanceBox.Header>
                    )}
                    <GuidanceBox.Content css={{ display: 'grid', gap: 'space.100' }}>
                      <Text variant="text50" dangerouslySetInnerHTML={{ __html: recommendedMonthlySpending.body }} />
                    </GuidanceBox.Content>
                  </GuidanceBox>
                  <HStack css={{ alignItems: 'center', gap: 'space.50' }}>
                    <Text color="text.weak" variant="text50">
                      Does your info look correct?
                    </Text>
                    <Button
                      type="button"
                      variant="textPrimary"
                      css={{ fontSize: 'fontSize.50' }}
                      onClick={() => navigate('/profile')}
                    >
                      Review profile
                    </Button>
                  </HStack>
                </VStack>

                <Button type="submit" css={{ width: 'fit-content' }}>
                  Continue
                </Button>
              </VStack>
            </Form>
          </Formik>
        </VStack>
      </WorkflowLayout.Content>
      <WorkflowLayout.Footer>
        <Footer />
      </WorkflowLayout.Footer>
    </WorkflowLayout>
  );
};
